import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Container, Button, Dialog, DialogContent, DialogActions, Zoom, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadOrCreateAnon } from '../redux/actions/authActions';
import LoadingScreen from './LoadingScreen';
import { RED, RED_HOVER, DARK_TEXT } from '../utils/brandColors';

export default function GameRouteWrapper({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { userInfo } = useSelector((state) => state.userLogin);
  const [loading, setLoading] = useState(!userInfo);
  const [error, setError] = useState(null);
  // A deep link INTO a specific game (a scanned streamer QR, a shared invite
  // code) means the visitor came to do one thing right now. The "you're a
  // Guest, sign up" interstitial is friction that reads as another gate to
  // clear — suppress it for coded joins; it still greets casual /play entry.
  const isCodedJoin = /\/play\/(streamer|versus|agree|memory|mindmeld|dare)\/[^/]+/.test(location.pathname);
  const [popupOpen, setPopupOpen] = useState(() => {
    if (isCodedJoin) return false;
    try {
      return localStorage.getItem('guest_challenge_popup_dismissed') !== '1';
    } catch (_) {
      return true;
    }
  });

  const handleDismissPopup = () => {
    setPopupOpen(false);
    try {
      localStorage.setItem('guest_challenge_popup_dismissed', '1');
    } catch (_) {
      // localStorage might be blocked in incognito mode
    }
  };

  useEffect(() => {
    let blocked = false;
    try {
      blocked = localStorage.getItem('auth_blocked') === '1';
    } catch (_) {
      blocked = false;
    }

    if (blocked) {
      setError("You can't play games from this device.");
      setLoading(false);
      return;
    }

    if (!userInfo) {
      setLoading(true);
      dispatch(loadOrCreateAnon())
        .then((res) => {
          if (!res) {
            setError("Could not initialize guest session. Please check your connection.");
          }
          setLoading(false);
        })
        .catch(() => {
          setError("Could not initialize guest session. Please check your connection.");
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [userInfo, dispatch]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
            Unable to Play
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Go Home
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Dialog
        open={!!(userInfo?.isAnon && popupOpen)}
        onClose={handleDismissPopup}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 20px 48px rgba(0, 0, 0, 0.45)' 
              : '0 20px 48px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2.5 }}>
          <Box
            component="img"
            src={theme.palette.mode === 'dark' ? '/guest_challenge_popup_dark.png' : '/guest_challenge_popup_light.png'}
            alt="Welcome"
            sx={{
              width: '100%',
              maxWidth: 240,
              aspectRatio: '1/1',
              objectFit: 'cover',
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                : '0 8px 32px rgba(0, 0, 0, 0.06)',
            }}
          />

          <Box>
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -0.5, mb: 1, color: 'text.primary' }}>
              Welcome to the Arena
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, px: 1 }}>
              You are playing as a <strong>Guest</strong>. Sign up to save your scores to the global leaderboard, keep track of your streaks, and challenge friends!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, flexDirection: 'column', gap: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              handleDismissPopup();
              navigate('/register');
            }}
            sx={{
              py: 1.5,
              fontWeight: 800,
              fontSize: '0.95rem',
              borderRadius: '12px',
              textTransform: 'none',
              bgcolor: RED,
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                bgcolor: RED_HOVER,
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              }
            }}
          >
            Claim My Profile
          </Button>
          <Button
            fullWidth
            onClick={handleDismissPopup}
            sx={{
              py: 1.25,
              fontWeight: 700,
              fontSize: '0.9rem',
              borderRadius: '12px',
              textTransform: 'none',
              color: 'text.secondary',
              border: '1.5px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              }
            }}
          >
            Continue as Guest
          </Button>
        </DialogActions>
      </Dialog>
      {children}

    </>
  );
}

GameRouteWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};
